import { Store } from 'vuex';
import { RootState } from '@/utils/types';
import { keycloak, keycloakB2B, keycloakB2C } from '@/services/auth/auth.service';

export function waitForRefreshToken(refreshToken: Function, isReady: Function, addSubscriber: Function, fetchData: () => Promise<any>): Promise<any> {
  refreshToken();
  let localResolve, localReject;
  const callback = async (resolve, _reject) => {
    if (isReady()) {
      return resolve(await fetchData());
    }
  };
  let promise = new Promise<any>((res, rej) => {
    localResolve = res;
    localReject = rej;
    callback(localResolve, localReject);
  });
  addSubscriber({
    promise,
    resolve: localResolve,
    reject: localReject,
    callback,
  });
  return promise;
}

export class TokenService {
  currentRequest: { [k: string]: Promise<any> | null } = {};

  constructor(private isWebview: boolean, private store: Store<RootState>) {}

  getToken(): string {
    return keycloak.token || keycloakB2C.token || keycloakB2B.token || '';
  }

  refreshToken(promiseFn: () => Promise<any>): Promise<any> {
    if (!this.isWebview) {
      return Promise.resolve();
    }
    return waitForRefreshToken(
      () => this.store.dispatch('auth/refreshSharedToken'),
      () => this.store.getters['auth/isTokenReady'],
      (subscriber) => this.store.dispatch('auth/addTokenSubscriber', subscriber),
      promiseFn,
    );
  }

  get shouldHandleRefreshToken(): boolean {
    return this.isWebview;
  }
}

export const interceptors = {
  authTokenInjector: (tokenService: TokenService) => async (config) => {
    const token = tokenService.getToken();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  clearSuccessfulRequest: (tokenService: TokenService) => (response) => {
    const requestId = response.config.headers['X-REQUEST-ID'];
    tokenService.currentRequest[requestId] = null;
    return response;
  },
  refreshTokenAndRetry:
    (tokenService: TokenService, statusCodes: number[] = [401]) =>
    (error, requestFn) => {
      if (tokenService.shouldHandleRefreshToken && error.response && statusCodes.includes(error.response.status)) {
        const requestId = error.config.headers['X-REQUEST-ID'];
        if (!tokenService.currentRequest[requestId]) {
          tokenService.currentRequest[requestId] = tokenService.refreshToken(requestFn.bind(null, error.config));
        }
        return tokenService.currentRequest[requestId];
      }
      return error;
    },
  handleServerError:
    (handleError: (error) => void, statusCodes: number[] = [409, 500]) =>
    (error, _requestFn) => {
      if (error && error.response && statusCodes.includes(error.response.status)) {
        handleError(error);
      }
      return error;
    },
};

export const createUUID = () => {
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
};
